import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import { ConnectionCard } from '../components/ConnectionCard';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { socialMediaApi, SocialMediaStatus } from '../services/socialMediaApi';
import { useLocale } from '@/hooks/useLocale';

type DisconnectTarget = 'meta' | 'tiktok';

const DISCONNECT_META: DisconnectTarget = 'meta';
const DISCONNECT_TIKTOK: DisconnectTarget = 'tiktok';

interface ApprovalOutletContext {
  clientId: string;
  approvalToken?: string;
}

interface AvailablePage {
  id: string;
  pageId: string;
  pageName: string;
  connectionStatus: string;
  instagramAccount: {
    id: string;
    username: string;
  } | null;
}

export const SocialMediaSetupRoute: React.FC = () => {
  const { clientId, approvalToken } = useOutletContext<ApprovalOutletContext>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, tDynamic } = useLocale();

  const [status, setStatus] = useState<SocialMediaStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState<'facebook' | 'instagram' | 'tiktok' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Disconnect state
  const [disconnectTarget, setDisconnectTarget] = useState<DisconnectTarget | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState<DisconnectTarget | null>(null);

  // Page selection state
  const [availablePages, setAvailablePages] = useState<AvailablePage[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string>('');
  const [isSelectingPage, setIsSelectingPage] = useState(false);

  // Check for OAuth callback success/error
  const oauthSuccess = searchParams.get('success');
  const oauthError = searchParams.get('error');
  const selectPage = searchParams.get('select_page');
  const pageCount = searchParams.get('count');
  const tiktokConnected = searchParams.get('tiktok_connected');

  // Fetch available pages if OAuth completed with select_page param
  useEffect(() => {
    if (selectPage === 'true') {
      fetchAvailablePages();
    } else {
      fetchStatus();
    }
  }, [clientId, selectPage]);

  // Auto-redirect to dashboard after successful OAuth if both accounts are connected
  // BUT NOT if we're in page selection mode
  useEffect(() => {
    if (!selectPage && oauthSuccess && status?.facebook.connected && status?.instagram.connected) {
      // Give user a moment to see the success state, then redirect
      const timer = setTimeout(() => {
        navigate('..', { replace: true });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [selectPage, oauthSuccess, status, navigate]);

  const fetchStatus = async () => {
    try {
      setIsLoading(true);
      const data = await socialMediaApi.getStatus(clientId);
      setStatus(data);

      // Show success message if just completed OAuth
      if (oauthSuccess || tiktokConnected) {
        // Clear callback params from URL
        window.history.replaceState({}, '', window.location.pathname);
      }

      // Show error message if OAuth failed
      if (oauthError) {
        setError(getErrorMessage(oauthError));
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch (err) {
      console.error('Failed to fetch social media status:', err);
      setError(t('error.loadStatus'));
    } finally {
      setIsLoading(false);
    }
  };

  const getErrorMessage = (errorType: string): string => {
    return tDynamic(`error.${errorType}`, 'error.unknown');
  };

  const handleConnectFacebook = async () => {
    try {
      setIsConnecting('facebook');
      setError(null);

      const { oauthUrl } = await socialMediaApi.connectFacebook(clientId);

      // Open OAuth in same window (will redirect back)
      window.location.href = oauthUrl;
    } catch (err) {
      console.error('Failed to initiate Facebook connection:', err);
      setError(t('error.connectFacebook'));
      setIsConnecting(null);
    }
  };

  const handleConnectTiktok = async () => {
    try {
      setIsConnecting('tiktok');
      setError(null);

      const { oauthUrl } = await socialMediaApi.connectTiktok(clientId);

      window.location.href = oauthUrl;
    } catch (err) {
      console.error('Failed to initiate TikTok connection:', err);
      setError(t('error.connectTiktok'));
      setIsConnecting(null);
    }
  };

  const handleRetryInstagram = async () => {
    if (!status?.facebook.pages[0]) return;

    try {
      setIsConnecting('instagram');
      setError(null);

      const result = await socialMediaApi.retryInstagram(
        clientId,
        status.facebook.pages[0].id
      );

      if (result.success) {
        // Refresh status
        await fetchStatus();
      } else if (result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      console.error('Failed to retry Instagram connection:', err);
      setError(t('error.connectInstagram'));
    } finally {
      setIsConnecting(null);
    }
  };

  const handleSkipToApproval = async () => {
    try {
      await socialMediaApi.skipInstagram(clientId);
      navigate('..', { replace: true });
    } catch (err) {
      console.error('Failed to skip Instagram:', err);
      navigate('..', { replace: true });
    }
  };

  const handleDisconnectMeta = async () => {
    setDisconnectTarget(null);
    setIsDisconnecting(DISCONNECT_META);
    try {
      await socialMediaApi.disconnect(clientId);
      await fetchStatus();
    } catch (err) {
      console.error('Failed to disconnect Meta accounts:', err);
      setError(t('error.disconnectMeta'));
    } finally {
      setIsDisconnecting(null);
    }
  };

  const handleDisconnectTiktok = async () => {
    setDisconnectTarget(null);
    setIsDisconnecting(DISCONNECT_TIKTOK);
    try {
      await socialMediaApi.disconnectTiktok(clientId);
      await fetchStatus();
    } catch (err) {
      console.error('Failed to disconnect TikTok:', err);
      setError(t('error.disconnectTiktok'));
    } finally {
      setIsDisconnecting(null);
    }
  };

  const handleContinueToApproval = () => {
    navigate('..', { replace: true });
  };

  const fetchAvailablePages = async () => {
    try {
      setIsLoading(true);
      const data = await socialMediaApi.getAvailablePages(clientId);
      setAvailablePages(data.pages);

      // Auto-select if only one page
      if (data.pages.length === 1) {
        setSelectedPageId(data.pages[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch available pages:', err);
      setError(t('error.loadPages'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPage = async () => {
    if (!selectedPageId) {
      setError(t('setup.selectPage.error'));
      return;
    }

    try {
      setIsSelectingPage(true);
      setError(null);

      const result = await socialMediaApi.selectPage(clientId, selectedPageId);

      if (result.success) {
        // Clear all search params (removes select_page and count)
        setSearchParams({}, { replace: true });

        // Fetch status will be triggered by useEffect when selectPage becomes null
      }
    } catch (err) {
      console.error('Failed to select page:', err);
      setError(t('error.selectPage'));
    } finally {
      setIsSelectingPage(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-2xl mx-auto px-4 lg:px-6 py-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-gray-600">{t('setup.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  const facebookConnected = status?.facebook.connected || false;
  const instagramConnected = status?.instagram.connected || false;
  const tiktokConnectedStatus = status?.tiktok?.connected || false;
  const facebookPage = status?.facebook.pages[0];
  const instagramAccount = status?.instagram.accounts[0];
  const tiktokAccount = status?.tiktok?.accounts[0];

  // Find platform-specific errors
  const instagramError = status?.errors.find(e => e.platform === 'instagram');
  const tiktokError = status?.errors.find(e => e.platform === 'tiktok');

  return (
    <div className="w-full max-w-2xl mx-auto px-4 lg:px-6 py-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('setup.title')}
        </h1>
        <p className="text-gray-600">
          {t('setup.subtitle')}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Page Selection UI - Show when OAuth callback includes select_page param */}
      {selectPage === 'true' && availablePages.length > 0 && (
        <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('setup.selectPage.title')}
            </h2>
            <p className="text-sm text-gray-600">
              {availablePages.length} {availablePages.length === 1 ? t('setup.selectPage.page') : t('setup.selectPage.pages')}{' '}
              {t('setup.selectPage.subtitle')}
            </p>
          </div>

          <div className="space-y-3">
            {availablePages.map((page) => (
              <label
                key={page.id}
                className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedPageId === page.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="facebook_page"
                  value={page.id}
                  checked={selectedPageId === page.id}
                  onChange={(e) => setSelectedPageId(e.target.value)}
                  className="w-4 h-4 text-blue-600"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{page.pageName}</div>
                  {page.instagramAccount && (
                    <div className="text-sm text-gray-600 mt-1">
                      Instagram: @{page.instagramAccount.username}
                    </div>
                  )}
                </div>
                {selectedPageId === page.id && (
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </label>
            ))}
          </div>

          <button
            onClick={handleSelectPage}
            disabled={!selectedPageId || isSelectingPage}
            className="mt-4 w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            {isSelectingPage ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('setup.selectPage.connecting')}
              </>
            ) : (
              <>
                {t('setup.selectPage.confirm')}
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      )}

      {/* Regular connection cards - Hide during page selection */}
      {selectPage !== 'true' && (
        <div className="space-y-4">
        {/* Facebook + Instagram grouped card */}
        <div className={`border rounded-lg p-6 ${facebookConnected ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 border-white ${facebookConnected ? 'bg-green-100' : 'bg-blue-100'}`}>
                  {facebookConnected ? (
                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="text-sm font-bold text-blue-800">f</span>
                  )}
                </div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 border-white ${instagramConnected ? 'bg-green-100' : 'bg-pink-100'}`}>
                  {instagramConnected ? (
                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="text-sm font-bold text-pink-800">IG</span>
                  )}
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-lg text-gray-900">{t('setup.meta.title')}</h3>
                {facebookConnected ? (
                  <p className="text-sm text-gray-600">
                    {facebookPage?.pageName}
                    {instagramAccount ? ` / @${instagramAccount.username}` : ''}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">{t('setup.meta.notConnected')}</p>
                )}
              </div>
            </div>

            {!facebookConnected && (
              <button
                onClick={handleConnectFacebook}
                disabled={isConnecting === 'facebook'}
                className={`px-6 py-2.5 text-white rounded-lg font-medium transition-colors ${
                  isConnecting === 'facebook' ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isConnecting === 'facebook' ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('setup.meta.connecting')}
                  </span>
                ) : t('setup.meta.connectFacebook')}
              </button>
            )}

            {facebookConnected && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-green-600">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">{t('setup.meta.connected')}</span>
                </div>
                <button
                  onClick={() => setDisconnectTarget(DISCONNECT_META)}
                  disabled={isDisconnecting === DISCONNECT_META}
                  className="px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isDisconnecting === DISCONNECT_META ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('setup.meta.disconnecting')}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      {t('setup.meta.disconnect')}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Instagram sub-section when FB connected but IG not */}
          {facebookConnected && !instagramConnected && (
            <div className="ml-[52px] mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center justify-between">
                <p className="text-sm text-yellow-800">{t('setup.instagram.notConnected')}</p>
                <button
                  onClick={handleRetryInstagram}
                  disabled={isConnecting === 'instagram'}
                  className="px-4 py-1.5 text-sm bg-pink-600 hover:bg-pink-700 text-white rounded-lg font-medium transition-colors disabled:bg-gray-400"
                >
                  {isConnecting === 'instagram' ? t('setup.instagram.connecting') : t('setup.instagram.connect')}
                </button>
              </div>
            </div>
          )}

          {/* Instagram error */}
          {instagramError && (
            <div className="ml-[52px] mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">{instagramError.errorMessage}</p>
              <button
                onClick={handleRetryInstagram}
                disabled={isConnecting === 'instagram'}
                className="mt-2 text-xs text-yellow-800 underline hover:text-yellow-900 font-medium"
              >
                {t('setup.instagram.retry')}
              </button>
            </div>
          )}

          {/* Follower counts */}
          {facebookConnected && instagramAccount?.followersCount != null && (
            <div className="ml-[52px] mt-2">
              <p className="text-xs text-gray-500">
                {instagramAccount.followersCount.toLocaleString()} follower Instagram
              </p>
            </div>
          )}
        </div>

        {/* TikTok Connection Card */}
        <ConnectionCard
          platform="TikTok"
          connected={tiktokConnectedStatus}
          onConnect={handleConnectTiktok}
          onDisconnect={() => setDisconnectTarget(DISCONNECT_TIKTOK)}
          data={tiktokAccount ? {
            username: tiktokAccount.displayName || tiktokAccount.username || undefined,
            followersCount: tiktokAccount.followersCount,
          } : undefined}
          error={tiktokError ? {
            type: tiktokError.errorType,
            message: tiktokError.errorMessage,
            troubleshootingSteps: [],
          } : undefined}
          canRetry={true}
          loading={isConnecting === 'tiktok'}
          disconnecting={isDisconnecting === DISCONNECT_TIKTOK}
        />
        </div>
      )}

      {/* Navigation Buttons - Hide during page selection */}
      {selectPage !== 'true' && (
      <div className="flex justify-between items-center mt-8 pt-6 border-t">
        {facebookConnected && !instagramConnected && (
          <button
            onClick={handleSkipToApproval}
            className="px-6 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
          >
            {t('setup.skipInstagram')}
          </button>
        )}

        {facebookConnected && instagramConnected && (
          <button
            onClick={handleContinueToApproval}
            className="ml-auto px-6 py-2.5 text-white bg-green-600 hover:bg-green-700 rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            {t('setup.goToApproval')}
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        )}
      </div>
      )}

      {/* Help Text - Hide during page selection */}
      {selectPage !== 'true' && !facebookConnected && (
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex gap-3">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-blue-800 font-medium mb-2">
                {t('setup.help.title')}
              </p>
              <p className="text-sm text-blue-700">
                {t('setup.help.body')}
              </p>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={disconnectTarget !== null}
        onClose={() => setDisconnectTarget(null)}
        onConfirm={disconnectTarget === DISCONNECT_META ? handleDisconnectMeta : handleDisconnectTiktok}
        title={disconnectTarget === DISCONNECT_META ? t('setup.disconnect.metaTitle') : t('setup.disconnect.tiktokTitle')}
        message={
          disconnectTarget === DISCONNECT_META
            ? t('setup.disconnect.metaMessage')
            : t('setup.disconnect.tiktokMessage')
        }
        confirmText={t('setup.disconnect.confirm')}
        variant="danger"
        isLoading={isDisconnecting !== null}
      />
    </div>
  );
};
